import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, Copy } from "lucide-react";
import { api } from "@/lib/api";
import type { FavoriteItem } from "@/components/pages/FavoritesPage";
import { Blue2Page, Part } from "../Page";
import { CLUB, Draw, EASE, HEART, Ink, VIEW } from "../bits";
import "./Favorites.css";

/* blue-2 favorites: Chelsea's real list from /api/favorites (same data and
   link handling as the current /favorites page), filtered by category, with
   featured picks on a clothesline and every discount code copyable. */

const ALL = "all";

/** Same ordering as the current favorites page: sortOrder, then title. */
function ordered(items: FavoriteItem[]) {
  return [...items].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.title.localeCompare(b.title));
}

/** Strip srcset width descriptors some legacy image URLs carry. */
const imageUrl = (raw?: string | null) => raw?.trim().split(/\s+/)[0] || null;

function useFavorites() {
  const [items, setItems] = useState<FavoriteItem[] | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    api.get<{ items: FavoriteItem[] }>("/api/favorites").then(r => setItems(ordered(r.items))).catch(() => setError(true));
  }, []);
  return { items, error };
}

/** Outbound favorites open in a new tab; internal ones route in-app. */
function Out({ item, className, children, label }: { item: FavoriteItem; className: string; children: React.ReactNode; label?: string }) {
  return item.openInNewTab
    ? <a href={item.href} target="_blank" rel="noopener noreferrer" className={className} aria-label={label}>{children}</a>
    : <Link to={item.href} className={className} aria-label={label}>{children}</Link>;
}

function Picture({ item }: { item: FavoriteItem }) {
  const [failed, setFailed] = useState(false);
  const src = imageUrl(item.imageUrl);
  // Stores are mostly logos, so they sit on a swatch instead of filling the frame.
  const logo = item.tag === "store" || !!src?.match(/logo|\.svg/i);
  if (!src || failed) return <span className="bf-pic bf-pic-blank" aria-hidden="true"><b>{item.title.charAt(0)}</b></span>;
  return <span className={`bf-pic${logo ? " bf-pic-logo" : ""}`}>
    <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />
  </span>;
}

/** A stitched hang-tag that copies the discount code. */
function CodeTag({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  async function copy() {
    try { await navigator.clipboard.writeText(code); } catch { /* clipboard blocked: the code stays visible to copy by hand */ }
    setCopied(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 1800);
  }
  return <button type="button" className={`bf-code${copied ? " is-copied" : ""}`} onClick={copy} aria-label={`Copy discount code ${code}`}>
    <i aria-hidden="true" />
    <span className="bf-code-label">code</span>
    <b>{code}</b>
    {copied ? <Check size={15} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
    <span className="bf-code-done" role="status">{copied ? "copied!" : ""}</span>
  </button>;
}

const TILT = [-1.6, 1.2, -0.8, 1.8, -1.2, 0.9];
const TAPE = ["soft", "pop", "extra"] as const;

function Card({ item, i }: { item: FavoriteItem; i: number }) {
  return <motion.li className="bf-card" style={{ rotate: `${TILT[i % TILT.length]}deg` }}
    initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }} transition={{ duration: 0.4, delay: Math.min(i, 8) * 0.04, ease: EASE }} layout>
    <i className={`bf-tape bf-tape-${TAPE[i % 3]}`} aria-hidden="true" />
    <Out item={item} className="bf-card-pic" label={`${item.title} (${item.categoryName})`}><Picture item={item} /></Out>
    <span className="bf-card-cat">{item.categoryName}</span>
    <h3><Out item={item} className="bf-card-title">{item.title}</Out></h3>
    {item.description && <p className="bf-card-note">{item.description}</p>}
    <div className="bf-card-foot">
      {item.discountCode && <CodeTag code={item.discountCode} />}
      <Out item={item} className="bf-card-go">{item.ctaLabel === "Visit link" ? "Shop" : item.ctaLabel}<ArrowUpRight size={15} aria-hidden="true" /></Out>
    </div>
  </motion.li>;
}

/** Featured picks hang from a clothesline, like the homepage. */
function Line({ items }: { items: FavoriteItem[] }) {
  const rows = [items.slice(0, 3), items.slice(3, 6)].filter(r => r.length);
  return <div className="bf-lines">
    {rows.map((row, r) => <div key={r} className="bf-line">
      <svg className="bf-line-string" viewBox="0 0 1000 60" preserveAspectRatio="none" aria-hidden="true"><path d="M-20 6 Q500 74 1020 6" /></svg>
      <ul>{row.map((item, i) => { const tilt = [-4, 2, 5][i]; return <motion.li key={item.id} style={{ marginTop: [27, 40, 27][i] }}
        initial={{ opacity: 0, y: -30, rotate: tilt * 2 }} whileInView={{ opacity: 1, y: 0, rotate: tilt }} whileHover={{ rotate: [tilt, tilt - 5, tilt + 3, tilt] }} viewport={VIEW}
        transition={{ delay: i * 0.12, type: "spring", stiffness: 120, damping: 10 }}>
        <i className="bf-pin" aria-hidden="true" />
        <div className="bf-fave">
          <Out item={item} className="bf-card-pic" label={item.title}><Picture item={item} /></Out>
          <span className="bf-card-cat">{item.categoryName}</span>
          <h3><Out item={item} className="bf-card-title">{item.title}</Out></h3>
          {item.description && <p className="bf-card-note">{item.description}</p>}
          {item.discountCode && <CodeTag code={item.discountCode} />}
        </div>
      </motion.li>; })}</ul>
    </div>)}
  </div>;
}

function Header({ items }: { items: FavoriteItem[] | null }) {
  const codes = items?.filter(i => i.discountCode).length ?? 0;
  return <div className="bf-head">
    <h1 className="bf-title">
      <motion.span className="bf-title-hand" initial={{ opacity: 0, rotate: -12 }} animate={{ opacity: 1, rotate: -5 }} transition={{ delay: 0.3, duration: 0.5, ease: EASE }}>stuff I</motion.span>
      <span className="bf-title-word" aria-label="love">
        {"love".split("").map((c, i) => <motion.span key={i} aria-hidden="true" initial={{ y: "18%", opacity: 0 }} animate={{ y: "0%", opacity: 1 }} transition={{ delay: 0.1 + i * 0.06, duration: 0.6, ease: EASE }}>{c}</motion.span>)}
      </span>
    </h1>
    <svg className="bf-head-heart" viewBox="0 0 30 46" aria-hidden="true"><Draw d={HEART} delay={0.8} width={3.4} /></svg>
    <motion.div className="bf-head-side" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.5, ease: EASE }}>
      <p className="bf-head-note">The tools, machines, fabric shops (and a few treats) I actually use and would buy again.</p>
      {items && items.length > 0 && <ul className="bf-stats" aria-label="At a glance">
        <li><b>{items.length}</b> favorites</li>
        {codes > 0 && <li><b>{codes}</b> discount codes</li>}
      </ul>}
      {codes > 0 && <p className="bf-head-hand">tap a code to copy it <span aria-hidden="true">♥</span></p>}
    </motion.div>
  </div>;
}

export function Blue2Favorites() {
  const { items, error } = useFavorites();
  const [cat, setCat] = useState(ALL);
  const [codesOnly, setCodesOnly] = useState(false);

  const cats = useMemo(() => {
    const m = new Map<string, { label: string; n: number }>();
    for (const i of items ?? []) m.set(i.categorySlug, { label: i.categoryName, n: (m.get(i.categorySlug)?.n ?? 0) + 1 });
    return [...m.entries()].sort((a, b) => a[1].label.localeCompare(b[1].label));
  }, [items]);

  const browsing = cat === ALL && !codesOnly;
  const featured = useMemo(() => (items ?? []).filter(i => i.featured).slice(0, 6), [items]);
  const shown = useMemo(() => (items ?? []).filter(i =>
    (cat === ALL || i.categorySlug === cat) && (!codesOnly || i.discountCode) && !(browsing && featured.includes(i))
  ), [items, cat, codesOnly, browsing, featured]);

  return <Blue2Page current="favorites" tone="periwinkle" header={<Header items={items} />}>
    <Part p="faves">
      <section className="b2-sec bf" aria-labelledby="bf-list-title">
        <div className="b2-wrap">
          <h2 id="bf-list-title" className="cb-sr">All my favorites</h2>
          {error && <p className="bf-state" role="alert">My favorites didn’t load. Try again in a minute? <span className="bf-state-hand">(it’s not you, it’s the internet)</span></p>}

          {!error && <>
            <div className="bf-filters" role="group" aria-label="Filter favorites">
              <button type="button" aria-pressed={cat === ALL} onClick={() => setCat(ALL)}>Everything{items && <small>{items.length}</small>}</button>
              {cats.map(([slug, c]) => <button key={slug} type="button" aria-pressed={cat === slug} onClick={() => setCat(slug)}>{c.label}<small>{c.n}</small></button>)}
              <button type="button" className="bf-filter-codes" aria-pressed={codesOnly} onClick={() => setCodesOnly(v => !v)}>
                <i aria-hidden="true" />Has a discount code
              </button>
            </div>

            {items === null && <ul className="bf-grid" aria-label="Loading favorites">{Array.from({ length: 8 }, (_, i) => <li key={i} className="bf-card bf-skel" aria-hidden="true" />)}</ul>}

            {items && browsing && featured.length > 0 && <div className="bf-featured">
              <div className="bf-section-head">
                <p className="b2-note bf-section-hand">my can’t-live-without picks</p>
                <svg className="bf-section-arrow" viewBox="0 0 80 60" aria-hidden="true"><Ink d="M10 6 C 20 30, 40 44, 66 48" delay={0.4} /><Ink d="M52 36 L67 48 L52 58" delay={0.8} /></svg>
              </div>
              <Line items={featured} />
            </div>}

            {items && <>
              {browsing && featured.length > 0 && <p className="b2-note bf-section-hand bf-rest-hand">and everything else I love</p>}
              {shown.length > 0
                ? <ul className="bf-grid"><AnimatePresence mode="popLayout">{shown.map((item, i) => <Card key={item.id} item={item} i={i} />)}</AnimatePresence></ul>
                : <p className="bf-state">Nothing here yet. <button type="button" className="bf-state-reset" onClick={() => { setCat(ALL); setCodesOnly(false); }}>Show everything</button></p>}
            </>}
          </>}

          <p className="bf-disclose">Some links are affiliate links. If you buy through them, I may earn a small commission at no extra cost to you. Codes are from the brands and can change.</p>
        </div>
      </section>
    </Part>

    <Part p="loop">
      <section className="b2-sec bf-nudge" aria-labelledby="bf-nudge-title">
        <div className="b2-wrap bf-nudge-inner">
          <div>
            <p className="b2-note bf-nudge-hand">got the tools?</p>
            <h2 id="bf-nudge-title" className="bf-nudge-title">Come use them with me.</h2>
            <p className="bf-nudge-copy">Twice a month I teach one bigger project live in the Loose Threads Club. Bring your favorite tools, your fabric, and your questions.</p>
          </div>
          <Link to={CLUB} className="b2-btn b2-btn-blue bf-nudge-btn">See the club <ArrowRight size={18} aria-hidden="true" /></Link>
        </div>
      </section>
    </Part>
  </Blue2Page>;
}
